import {Component, OnInit} from '@angular/core';
import {CheckboxModule} from "primeng/checkbox";
import {SelectChangeEvent, SelectModule} from "primeng/select";
import {InputTextModule} from "primeng/inputtext";
import {NgClass, NgIf} from "@angular/common";
import {FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from "@angular/forms";
import {Button} from "primeng/button";
import {Ripple} from "primeng/ripple";
import {IGroup} from "../../../core/models/group.model";
import {CustomDialogData} from "../../../core/models/custom-dialog.model";
import {DialogService, DynamicDialog, DynamicDialogRef} from "primeng/dynamicdialog";
import {UserService} from "../../../core/services/user.service";
import {GroupService} from "../../../core/services/group.service";
import {ToastService} from "../../../core/services/toast.service";
import {IUser} from "../../../core/models/user.model";
import {Direction, getUnpagedPageable, Sort} from "../../../core/models/page.model";
import {InstitutionService} from "../../../core/services/institution.service";
import {IInstitution} from "../../../core/models/institution.model";

@Component({
    selector: 'app-user-details',
    imports: [
        // Modules
        CheckboxModule, SelectModule, InputTextModule, ReactiveFormsModule, Button, Ripple,
        // Pipes & Directives
        NgIf, NgClass,
    ],
    templateUrl: './user-details.component.html',
    styleUrl: './user-details.component.scss'
})
export class UserDetailsComponent implements OnInit {

  formGroup = new FormGroup({
    firstname: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(50)]
    }),
    lastname: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(50)]
    }),
    username: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(3), Validators.maxLength(30)]
    }),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email]
    }),
    phoneNumber: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, this.phoneValidator.bind(this)]
    }),
    group: new FormControl<string>('', {nonNullable: true, validators: [Validators.required]}),
    institution: new FormControl<string>('', {nonNullable: true, validators: [Validators.required]}),
    enabled: new FormControl(true, {nonNullable: true}),
  });

  boundData: CustomDialogData | undefined;
  isLoading: boolean = false;
  instance: DynamicDialog | undefined;
  institutions: IInstitution[] = [];
  groups: IGroup[] = [];
  user: IUser = {} as IUser;
  groupSort: Sort = {
    direction: Direction.ASC,
    orders: [
      {
        property: 'name',
        ignoreCase: false,
      },
    ],
  }
  institution: IInstitution = {} as IInstitution;

  constructor(private userService: UserService, private dialogService: DialogService, private groupService: GroupService,
              private institutionService: InstitutionService, private toastService: ToastService, private ref: DynamicDialogRef) {
    this.instance = this.dialogService.getInstance(this.ref);
  }

  ngOnInit() {
    this.loadInstitutions();
    this.boundData = this.instance?.data;
    this.user = this.boundData?.data as IUser;
    if ((this.boundData?.isEdit || this.boundData?.isView) && this.boundData.data) {
      this.formGroup.patchValue(this.boundData.data);
      this.formGroup.patchValue({ group: this.user.group.id, institution: this.user.group.institution.id });
      this.getGroupsForInstitution(this.user.group.institution.id);
    }
    if (this.boundData?.isView) this.formGroup.disable();
  }

  loadInstitutions() {
    this.institutionService.getInstitutionByEnabled(true, getUnpagedPageable()).subscribe({
      next: (page) => this.institutions = page.content,
      error: () => {
        this.toastService.showToast('Récupération des institutions', 'Une erreur est survenue lors de la récupération des institutions. Veuillez réessayer plus tard.', 'error');
      }
    });
  }

  getGroupsForInstitution(id: string) {
    this.groupService.getGroupsByInstitutionId(id, getUnpagedPageable(this.groupSort)).subscribe({
      next: (page) => this.groups = page.content,
      error: () => {
        this.toastService.showToast('Récupération des groupes', 'Une erreur est survenue lors de la récupération des groupes. Veuillez réessayer plus tard.', 'error');
      }
    });
  }

  /**
   * Validateur personnalisé pour le téléphone
   */
  private phoneValidator(control: FormControl): ValidationErrors | null {
    if (!control.value) return null;

    const phoneStr = control.value.toString().trim();

    // Vérifier que le numéro ne contient que des chiffres, espaces, + et -
    if (!/^[\+\d\s\-]+$/.test(phoneStr)) {
      return { phoneInvalid: true };
    }

    // Extraire uniquement les chiffres
    const digitsOnly = phoneStr.replace(/[\s\-\+]/g, '');

    // Vérifier qu'il y a entre 8 et 15 chiffres
    if (digitsOnly.length < 8 || digitsOnly.length > 15) {
      return { phoneLength: true };
    }

    return null;
  }

  /**
   * Récupère le message d'erreur pour un champ donné
   */
  getFieldError(fieldName: string): string {
    const control = this.formGroup.get(fieldName);
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    const errors = control.errors;
    if (errors['required']) return 'Ce champ est requis';
    if (errors['email']) return 'Adresse email invalide';
    if (errors['minlength']) return `Minimum ${errors['minlength'].requiredLength} caractères`;
    if (errors['maxlength']) return `Maximum ${errors['maxlength'].requiredLength} caractères`;
    if (errors['phoneInvalid']) return 'Le numéro doit contenir uniquement des chiffres, espaces, + et -';
    if (errors['phoneLength']) return 'Le numéro doit contenir entre 8 et 15 chiffres';

    return 'Valeur invalide';
  }

  /**
   * Vérifie si un champ a une erreur et a été touché
   */
  hasFieldError(fieldName: string): boolean {
    const control = this.formGroup.get(fieldName);
    return !!(control && control.errors && control.touched);
  }

  /**
   * Marque tous les champs comme touchés pour afficher les erreurs
   */
  private markAllFieldsAsTouched(): void {
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Gère les erreurs HTTP et affiche un message approprié
   */
  private handleHttpError(error: any, operation: string): void {
    let errorMessage = 'Une erreur est survenue. Veuillez réessayer.';

    if (error.error) {
      if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error.error.message) {
        errorMessage = error.error.message;
      } else if (error.error.error) {
        errorMessage = error.error.error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Messages d'erreur spécifiques selon le code HTTP
    if (error.status === 400) {
      errorMessage = 'Données invalides. ' + errorMessage;
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else if (error.status === 403) {
      errorMessage = 'Accès refusé. Vous n\'avez pas les permissions nécessaires.';
    } else if (error.status === 404) {
      errorMessage = 'Utilisateur non trouvé.';
    } else if (error.status === 409) {
      errorMessage = 'Conflit. Un utilisateur avec ces informations existe déjà.';
    } else if (error.status === 500) {
      errorMessage = 'Erreur serveur. Veuillez contacter l\'administrateur.';
    } else if (error.status === 0) {
      errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
    }

    this.toastService.showToast(
      `Erreur lors de la ${operation}`,
      errorMessage,
      'error'
    );
  }

  create() {
    // Validation du formulaire avant soumission
    if (this.formGroup.invalid) {
      this.markAllFieldsAsTouched();
      this.toastService.showToast(
        'Formulaire invalide',
        'Veuillez corriger les erreurs avant de soumettre le formulaire',
        'warn'
      );
      return;
    }

    this.isLoading = true;
    const userDto = this.formGroup.value as unknown as IUser;
    userDto.group = this.groups.find(g => g.id == this.formGroup.value.group) as IGroup;
    this.userService.createUser(userDto).subscribe({
      next: (user) => {
        this.toastService.showToast('Utilisateur créé',`L'utilisateur ${user.firstname} ${user.lastname} a été créé avec succès.`);
        this.isLoading = false;
        this.ref.close();
      },
      error: (error) => {
        this.isLoading = false;
        this.handleHttpError(error, 'création');
      }
    });
  }

  update() {
    // Validation du formulaire avant soumission
    if (this.formGroup.invalid) {
      this.markAllFieldsAsTouched();
      this.toastService.showToast(
        'Formulaire invalide',
        'Veuillez corriger les erreurs avant de soumettre le formulaire',
        'warn'
      );
      return;
    }

    this.isLoading = true;
    const userDto = this.formGroup.value as unknown as IUser;
    userDto.group = this.groups.find(g => g.id == this.formGroup.value.group) as IGroup;
    this.userService.updateUser(this.boundData?.data.id, userDto).subscribe({
      next: (user) => {
        this.toastService.showToast('Utilisateur modifié',`L'utilisateur ${user.firstname} ${user.lastname} a été modifié avec succès.`);
        this.isLoading = false;
        this.ref.close();
      },
      error: (error) => {
        this.isLoading = false;
        this.handleHttpError(error, 'modification');
      }
    });
  }

  toggleUser() {
    const enabled = this.boundData?.data.enabled;
    this.userService.toggleUser(this.boundData?.data.id).subscribe({
      next: () => {
        this.toastService.showToast(enabled ? 'Utilisateur désactivé' : 'Utilisateur activé', `L'utilisateur a été ${enabled ? 'désactivé' : 'activé'} avec succès.`);
        this.ref.close();
      },
      error: (err) => {
        const action = enabled ? 'désactivation' : 'activation';
        this.handleHttpError(err, action);
      }
    });
  }

  loadGroups($event: SelectChangeEvent) {
    this.getGroupsForInstitution($event.value);
  }
}
